import { useSetSearchParam, useSetSearchParams } from '@/hooks/router';
import { PaginationInput } from 'gql-types';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type DynamicPaging = {
    page: number;
    setPage: (page: number) => void;
    pageSize: number;
    setPageSize: (pageSize: number) => void;
    pageSizeOptions: number[];
};

export const emptyPaginationByPage: PaginationByPage = {
    dynamicPaging: {
        page: 0,
        setPage() {
            // Do Nothing
        },
        pageSize: 25,
        setPageSize: () => {
            // Do Nothing
        },
        pageSizeOptions: [],
    },
    resetPage() {
        // Do Nothing
    },
    paginationInput: {
        offset: 0,
    },
    preloadNextPageInput: null,
    preloadPreviousPageInput: null,
};

export type PaginationByPage = {
    dynamicPaging: DynamicPaging;
    resetPage(): void;
    paginationInput: PaginationInput;
    // Next page input is null if there are no more pages
    preloadNextPageInput: PaginationInput | null;
    // Previous page input is null if the current page is the first page
    preloadPreviousPageInput: PaginationInput | null;
};

type DynamicPageSize = {
    containerRef?: HTMLElement;
    firstElement?: HTMLElement[];
};

type UsePaginationByPageOptions = {
    totalCount?: number;
    dynamicPageSize?: DynamicPageSize;
    defaultPageSize?: number;
    pageSizeOptions?: number[];
    withSearchParam?: boolean;
};

type UsePaginationByPage = (options?: UsePaginationByPageOptions) => PaginationByPage;

const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const getPage = (page?: string | null) => {
    if (!page) {
        return 0;
    }
    const parsed = Number.parseInt(page);
    if (Number.isNaN(parsed) || parsed < 1) {
        return 0;
    }
    return parsed - 1;
};

type UsePage = (
    withSearchParam?: boolean,
) => [
    page: number,
    setPage: Dispatch<SetStateAction<number>>,
    setPageState: Dispatch<SetStateAction<number>>,
];
const usePage: UsePage = (withSearchParam) => {
    const [pageSearchParam, setPageSearchParam] = useSetSearchParam('page');
    const [pageState, setPageState] = useState(getPage(pageSearchParam));
    const lastPageSearchParam = useRef(pageSearchParam);
    const pageRef = useRef(pageState);
    pageRef.current = pageState;

    useEffect(() => {
        if (withSearchParam && pageSearchParam !== lastPageSearchParam.current) {
            lastPageSearchParam.current = pageSearchParam;
            setPageState(getPage(pageSearchParam));
        }
    }, [pageSearchParam, withSearchParam]);

    const setPage = useCallback(
        (page: SetStateAction<number>) => {
            const nextValue = typeof page === 'function' ? page(pageRef.current) : page;
            setPageState(nextValue);
            if (withSearchParam) {
                const nextActualPage = nextValue + 1;
                setPageSearchParam(nextActualPage === 1 ? null : `${nextActualPage}`);
            }
        },
        [setPageSearchParam, withSearchParam],
    );

    return [pageState, setPage, setPageState];
};

const getPageSize = (
    param: string | null | undefined,
    pageSizeOptions: number[],
    defaultPageSize: number,
): number => {
    if (!param) {
        return defaultPageSize;
    }
    const parsed = Number.parseInt(param);
    if (Number.isNaN(parsed) || parsed < 1) {
        return defaultPageSize;
    }
    if (pageSizeOptions.length && !pageSizeOptions.includes(parsed)) {
        return defaultPageSize;
    }
    return parsed;
};

type UsePageSize = (
    withSearchParam: boolean | undefined,
    initialPageSize: number,
    pageSizeOptions: number[],
) => [
    pageSize: number,
    setPageSize: (size: number) => void,
    setPageSizeState: Dispatch<SetStateAction<number>>,
];
const usePageSize: UsePageSize = (withSearchParam, initialPageSize, pageSizeOptions) => {
    const [pageSizeSearchParam, setPageSizeSearchParam] = useSetSearchParam('pageSize');
    const [pageSizeState, setPageSizeState] = useState(
        withSearchParam
            ? getPageSize(pageSizeSearchParam, pageSizeOptions, initialPageSize)
            : initialPageSize,
    );
    const lastPageSizeSearchParam = useRef(pageSizeSearchParam);

    useEffect(() => {
        if (withSearchParam && pageSizeSearchParam !== lastPageSizeSearchParam.current) {
            lastPageSizeSearchParam.current = pageSizeSearchParam;
            setPageSizeState(getPageSize(pageSizeSearchParam, pageSizeOptions, initialPageSize));
        }
    }, [pageSizeSearchParam, withSearchParam, pageSizeOptions, initialPageSize]);

    const setPageSize = useCallback(
        (size: number) => {
            setPageSizeState(size);
            if (withSearchParam) {
                setPageSizeSearchParam(size === initialPageSize ? null : `${size}`);
            }
        },
        [withSearchParam, initialPageSize, setPageSizeSearchParam],
    );

    return [pageSizeState, setPageSize, setPageSizeState];
};

export const usePaginationByPage: UsePaginationByPage = (options): PaginationByPage => {
    const {
        totalCount,
        withSearchParam,
        defaultPageSize = DEFAULT_PAGE_SIZE,
        pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    } = options ?? {};
    const setSearchParams = useSetSearchParams();
    const [page, setPage, setPageState] = usePage(withSearchParam);
    const initialPageSize = pageSizeOptions.length
        ? pageSizeOptions.includes(defaultPageSize)
            ? defaultPageSize
            : pageSizeOptions[0]
        : DEFAULT_PAGE_SIZE;
    const [pageSize, , setPageSizeState] = usePageSize(
        withSearchParam,
        initialPageSize,
        pageSizeOptions,
    );

    // When page size changes, adjust the page number to keep the same starting offset.
    // Both the page and pageSize search params must be written in a single navigate call:
    // writing them through their separate setters builds two URLSearchParams from the same
    // stale `search` snapshot, so the second navigate clobbers the first's change.
    const setPageSize = useCallback(
        (newPageSize: number) => {
            const currentOffset = page * pageSize;
            const newPage = Math.floor(currentOffset / newPageSize);
            setPageState(newPage);
            setPageSizeState(newPageSize);
            if (withSearchParam) {
                const nextActualPage = newPage + 1;
                setSearchParams({
                    page: nextActualPage === 1 ? null : `${nextActualPage}`,
                    pageSize: newPageSize === initialPageSize ? null : `${newPageSize}`,
                });
            }
        },
        [
            page,
            pageSize,
            withSearchParam,
            initialPageSize,
            setPageState,
            setPageSizeState,
            setSearchParams,
        ],
    );

    const preloadNextPageInput = useMemo((): PaginationInput | null => {
        const nextOffset = (page + 1) * pageSize;
        if (typeof totalCount === 'number' && nextOffset >= totalCount) {
            return null;
        }
        return {
            limit: pageSize,
            offset: nextOffset,
        };
    }, [page, totalCount, pageSize]);

    const resetPage = useCallback(() => {
        setPage(0);
    }, [setPage]);

    const preloadPreviousPageInput = useMemo((): PaginationInput | null => {
        const previousOffset = (page - 1) * pageSize;
        if (previousOffset < 0) {
            return null;
        }
        return {
            limit: pageSize,
            offset: previousOffset,
        };
    }, [page, pageSize]);

    const dynamicPaging = useMemo(
        (): DynamicPaging => ({
            page,
            setPage,
            pageSize,
            setPageSize,
            pageSizeOptions,
        }),
        [page, pageSize, pageSizeOptions, setPage, setPageSize],
    );

    const paginationInput = useMemo(
        () => ({
            limit: pageSize,
            offset: page * pageSize,
        }),
        [page, pageSize],
    );

    return {
        dynamicPaging,
        paginationInput,
        resetPage,
        preloadNextPageInput,
        preloadPreviousPageInput,
    };
};
